import React, { useState } from 'react';
import { Zap, Eye, EyeOff, AlertCircle, Mail, CheckCircle, ArrowLeft, ArrowRight, Building2, User, Handshake } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';


const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France',
  'India', 'Singapore', 'UAE', 'South Africa', 'Brazil', 'Japan', 'Other'
];

const INDUSTRIES = [
  'Retail', 'Electronics', 'Groceries', 'Fashion', 'Health & Beauty',
  'Sports', 'Home & Living', 'Other'
];

const REFERRAL_SOURCES = [
  'Google', 'Social Media', 'Referral', 'Partner Network', 'Other'
];

const EXPECTED_SCANS = [
  '<1,000', '1,000–10,000', '10,000–100,000', '100,000+'
];

const TIERS = [
  {
    id: 'basic',
    name: 'Basic',
    price: 'Free',
    features: ['Up to 50 products', 'Basic analytics', 'Email support'],
    color: '#10B981',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$49/mo',
    features: ['Up to 500 products', 'Full analytics', 'Priority support'],
    color: '#00E5FF',
    popular: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    features: ['Unlimited products', 'Dedicated support', 'Custom integrations'],
    color: '#7B2FBE',
  },
];

const STEP_ICONS = [Building2, User, Handshake];
const STEP_LABELS = ['Business Info', 'Contact Details', 'Partnership'];

function ProgressBar({ step }) {
  return (
    <div className="flex items-center justify-between mb-8">
      {STEP_LABELS?.map((label, i) => {
        const Icon = STEP_ICONS?.[i];
        const isActive = i + 1 === step;
        const isDone = i + 1 < step;
        return (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center gap-1.5">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300"
                style={{
                  background: isDone ? '#10B981' : isActive ? 'linear-gradient(135deg, #00E5FF, #7B2FBE)' : 'rgba(255,255,255,0.05)',
                  border: isActive ? '2px solid #00E5FF' : isDone ? '2px solid #10B981' : '2px solid rgba(255,255,255,0.1)',
                }}
              >
                {isDone ? (
                  <CheckCircle size={16} className="text-white" />
                ) : (
                  <Icon size={15} style={{ color: isActive ? '#fff' : '#64748B' }} />
                )}
              </div>
              <span className="text-xs font-medium" style={{ color: isActive ? '#00E5FF' : isDone ? '#10B981' : '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{label}</span>
            </div>
            {i < 2 && (
              <div className="flex-1 h-px mx-2 mb-5" style={{ background: isDone ? '#10B981' : 'rgba(255,255,255,0.08)' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function PartnerAuth({ onAuthSuccess }) {
  const [authMode, setAuthMode] = useState('login'); // 'login' | 'apply' | 'forgot' | 'success'
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showPass, setShowPass] = useState(false);

  // Registration state
  const [regStep, setRegStep] = useState(1);
  const [regForm, setRegForm] = useState({
    company_name: '', industry: '', business_type: '', website: '', description: '',
    contact_first_name: '', contact_last_name: '', contact_email: '', phone: '', country: '', city: '',
    preferred_tier: 'pro', referral_source: '', expected_scans: '', agreed: false,
  });
  const [regError, setRegError] = useState('');
  const [submittedApp, setSubmittedApp] = useState(null);

  const handleLogin = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccessMsg('');
    setLoading(true);
    try {
      if (authMode === 'forgot') {
        const { error: resetErr } = await supabase?.auth?.resetPasswordForEmail(loginForm?.email, {
          redirectTo: `${window.location?.origin}/retail-partner-portal?reset=true`,
        });
        if (resetErr) { setError(resetErr?.message); }
        else { setSuccessMsg('Password reset email sent! Check your inbox.'); }
        return;
      }
      const { data, error: err } = await supabase?.auth?.signInWithPassword({
        email: loginForm?.email?.trim(),
        password: loginForm?.password,
      });
      if (err) { setError(err?.message); return; }
      const { data: profile, error: profileErr } = await supabase
        ?.from('partner_profiles')
        ?.select('*')
        ?.eq('id', data?.user?.id)
        ?.maybeSingle();
      if (profileErr || !profile) {
        setError('No partner account found for this email. Please use partner credentials.');
        await supabase?.auth?.signOut();
        return;
      }
      // Check onboarding status — redirect to wizard if not completed
      if (profile?.onboarding_completed === false) {
        onAuthSuccess(data?.user, profile, true);
        return;
      }
      onAuthSuccess(data?.user, profile);
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateStep = () => {
    if (regStep === 1) {
      if (!regForm?.company_name?.trim()) return 'Company name is required.';
      if (!regForm?.industry) return 'Please select an industry.';
      if (!regForm?.business_type) return 'Please select a business type.';
    }
    if (regStep === 2) {
      if (!regForm?.contact_first_name?.trim()) return 'First name is required.';
      if (!regForm?.contact_last_name?.trim()) return 'Last name is required.';
      if (!regForm?.contact_email?.trim() || !/^[^@]+@[^@]+\.[^@]+$/?.test(regForm?.contact_email)) return 'Valid business email is required.';
    }
    if (regStep === 3) {
      if (!regForm?.referral_source) return 'Please tell us how you heard about us.';
      if (!regForm?.expected_scans) return 'Please select expected monthly scans.';
      if (!regForm?.agreed) return 'You must agree to the Partner Terms & Conditions.';
    }
    return null;
  };

  const handleNextStep = () => {
    const err = validateStep();
    if (err) { setRegError(err); return; }
    setRegError('');
    setRegStep(s => s + 1);
  };

  const handleSubmitApplication = async () => {
    const err = validateStep();
    if (err) { setRegError(err); return; }
    setRegError('');
    setLoading(true);
    try {
      const { data: app, error: insertErr } = await supabase
        ?.from('partner_applications')
        ?.insert({
          company_name: regForm?.company_name,
          industry: regForm?.industry,
          business_type: regForm?.business_type,
          website: regForm?.website,
          description: regForm?.description,
          contact_first_name: regForm?.contact_first_name,
          contact_last_name: regForm?.contact_last_name,
          contact_email: regForm?.contact_email,
          phone: regForm?.phone,
          country: regForm?.country,
          city: regForm?.city,
          preferred_tier: regForm?.preferred_tier,
          referral_source: regForm?.referral_source,
          expected_scans: regForm?.expected_scans,
          application_status: 'pending',
        })
        ?.select()
        ?.single();

      if (insertErr) { setRegError(insertErr?.message); return; }

      // Trigger email notifications (fire and forget)
      supabase?.functions?.invoke('partner-application-email', {
        body: {
          company_name: regForm?.company_name,
          contact_first_name: regForm?.contact_first_name,
          contact_email: regForm?.contact_email,
          preferred_tier: regForm?.preferred_tier,
          industry: regForm?.industry,
          reference_number: app?.id?.substring(0, 8)?.toUpperCase(),
        },
      })?.catch(() => {});

      setSubmittedApp(app);
      setAuthMode('success');
    } catch (e) {
      setRegError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const switchAuthMode = (mode) => {
    setAuthMode(mode);
    setError('');
    setSuccessMsg('');
    setRegError('');
    setRegStep(1);
  };

  const inputStyle = {
    background: 'rgba(0,0,0,0.3)',
    border: '1px solid rgba(0,229,255,0.15)',
    fontFamily: 'DM Sans, sans-serif',
  };

  const labelStyle = { color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#0D1B2A' }}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(0,229,255,0.08), transparent 70%)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full" style={{ background: 'radial-gradient(circle, rgba(123,47,190,0.1), transparent 70%)' }} />
      </div>
      <div className="relative w-full" style={{ maxWidth: authMode === 'apply' ? '560px' : '440px' }}>
        {/* Header */}
        <div className="text-center mb-7">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4" style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.15), rgba(123,47,190,0.15))', border: '1px solid rgba(0,229,255,0.3)' }}>
            <Zap size={26} style={{ color: '#00E5FF' }} />
          </div>
          <h1 className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>FinVisAR</h1>
          <p className="text-sm font-medium" style={{ color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>Retail Partner Portal</p>
        </div>

        {/* Tab Toggle (Login / Apply) */}
        {authMode !== 'success' && authMode !== 'forgot' && (
          <div className="flex rounded-xl p-1 mb-6" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.1)' }}>
            {[{ id: 'login', label: 'Login' }, { id: 'apply', label: 'Apply Now' }]?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => switchAuthMode(tab?.id)}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200"
                style={{
                  background: authMode === tab?.id ? 'linear-gradient(135deg, #00E5FF22, #7B2FBE22)' : 'transparent',
                  color: authMode === tab?.id ? '#00E5FF' : '#64748B',
                  border: authMode === tab?.id ? '1px solid rgba(0,229,255,0.25)' : '1px solid transparent',
                  fontFamily: 'DM Sans, sans-serif',
                }}
              >
                {tab?.label}
              </button>
            ))}
          </div>
        )}

        {/* ── LOGIN FORM ── */}
        {authMode === 'login' && (
          <div className="rounded-2xl p-7" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,229,255,0.12)', backdropFilter: 'blur(20px)' }}>
            <h2 className="text-lg font-semibold text-white mb-5" style={{ fontFamily: 'DM Sans, sans-serif' }}>Sign in to your account</h2>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-xs font-medium mb-1.5" style={labelStyle}>Email Address</label>
                <input type="email" value={loginForm?.email} onChange={e => setLoginForm(f => ({ ...f, email: e?.target?.value }))} placeholder="partner@company.com" required
                  className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
                  style={inputStyle} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium" style={labelStyle}>Password</label>
                  <button type="button" onClick={() => switchAuthMode('forgot')} className="text-xs" style={{ color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>Forgot password?</button>
                </div>
                <div className="relative">
                  <input type={showPass ? 'text' : 'password'} value={loginForm?.password} onChange={e => setLoginForm(f => ({ ...f, password: e?.target?.value }))} placeholder="••••••••" required
                    className="w-full px-4 py-3 pr-11 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
                    style={inputStyle} />
                  <button type="button" onClick={() => setShowPass(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: '#64748B' }}>
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              {error && (
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
                  <AlertCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#EF4444' }} />
                  <p className="text-xs" style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>{error}</p>
                </div>
              )}
              {successMsg && (
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' }}>
                  <CheckCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#10B981' }} />
                  <p className="text-xs" style={{ color: '#10B981', fontFamily: 'DM Sans, sans-serif' }}>{successMsg}</p>
                </div>
              )}
              <button type="submit" disabled={loading}
                className="w-full py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 mt-2"
                style={{ background: loading ? 'rgba(0,229,255,0.3)' : 'linear-gradient(135deg, #00E5FF, #7B2FBE)', fontFamily: 'DM Sans, sans-serif', opacity: loading ? 0.7 : 1 }}>
                {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : null}
                {loading ? 'Signing in...' : 'Sign In to Portal'}
              </button>
            </form>
          </div>
        )}

        {/* ── FORGOT PASSWORD ── */}
        {authMode === 'forgot' && (
          <div className="rounded-2xl p-7" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,229,255,0.12)', backdropFilter: 'blur(20px)' }}>
            <h2 className="text-lg font-semibold text-white mb-5" style={{ fontFamily: 'DM Sans, sans-serif' }}>Reset your password</h2>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-xs font-medium mb-1.5" style={labelStyle}>Email Address</label>
                <input type="email" value={loginForm?.email} onChange={e => setLoginForm(f => ({ ...f, email: e?.target?.value }))} placeholder="partner@company.com" required
                  className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
                  style={inputStyle} />
              </div>
              {error && (
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
                  <AlertCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#EF4444' }} />
                  <p className="text-xs" style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>{error}</p>
                </div>
              )}
              {successMsg && (
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' }}>
                  <CheckCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#10B981' }} />
                  <p className="text-xs" style={{ color: '#10B981', fontFamily: 'DM Sans, sans-serif' }}>{successMsg}</p>
                </div>
              )}
              <button type="submit" disabled={loading}
                className="w-full py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2"
                style={{ background: loading ? 'rgba(0,229,255,0.3)' : 'linear-gradient(135deg, #00E5FF, #7B2FBE)', fontFamily: 'DM Sans, sans-serif', opacity: loading ? 0.7 : 1 }}>
                {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Mail size={15} />}
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
            <button onClick={() => switchAuthMode('login')} className="w-full mt-4 flex items-center justify-center gap-1.5 text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>
              <ArrowLeft size={13} /> Back to sign in
            </button>
          </div>
        )}

        {/* ── REGISTRATION FORM ── */}
        {authMode === 'apply' && (
          <div className="rounded-2xl p-7" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,229,255,0.12)', backdropFilter: 'blur(20px)' }}>
            <ProgressBar step={regStep} />

            {regError && (
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl mb-4" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
                <AlertCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#EF4444' }} />
                <p className="text-xs" style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>{regError}</p>
              </div>
            )}

            {/* Step 1: Business Information */}
            {regStep === 1 && (
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-white mb-4" style={{ fontFamily: 'DM Sans, sans-serif' }}>Business Information</h3>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={labelStyle}>Company Name <span style={{ color: '#EF4444' }}>*</span></label>
                  <input type="text" value={regForm?.company_name} onChange={e => setRegForm(f => ({ ...f, company_name: e?.target?.value }))} placeholder="Your company name"
                    className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={labelStyle}>Industry <span style={{ color: '#EF4444' }}>*</span></label>
                  <select value={regForm?.industry} onChange={e => setRegForm(f => ({ ...f, industry: e?.target?.value }))}
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none" style={{ ...inputStyle, color: regForm?.industry ? '#E2E8F0' : '#64748B' }}>
                    <option value="" style={{ background: '#0D1B2A' }}>Select industry</option>
                    {INDUSTRIES?.map(i => <option key={i} value={i} style={{ background: '#0D1B2A' }}>{i}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-2" style={labelStyle}>Business Type <span style={{ color: '#EF4444' }}>*</span></label>
                  <div className="grid grid-cols-2 gap-2">
                    {['Sole Trader', 'SME', 'Enterprise', 'Brand']?.map(type => (
                      <button key={type} type="button" onClick={() => setRegForm(f => ({ ...f, business_type: type }))}
                        className="py-2.5 px-3 rounded-xl text-xs font-medium text-left transition-all"
                        style={{
                          background: regForm?.business_type === type ? 'rgba(0,229,255,0.12)' : 'rgba(0,0,0,0.2)',
                          border: regForm?.business_type === type ? '1px solid rgba(0,229,255,0.4)' : '1px solid rgba(255,255,255,0.06)',
                          color: regForm?.business_type === type ? '#00E5FF' : '#94A3B8',
                          fontFamily: 'DM Sans, sans-serif',
                        }}>
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={labelStyle}>Website URL</label>
                  <input type="url" value={regForm?.website} onChange={e => setRegForm(f => ({ ...f, website: e?.target?.value }))} placeholder="https://yourcompany.com"
                    className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={labelStyle}>Company Description <span className="text-xs" style={{ color: '#475569' }}>({regForm?.description?.length || 0}/300)</span></label>
                  <textarea value={regForm?.description} onChange={e => setRegForm(f => ({ ...f, description: e?.target?.value?.slice(0, 300) }))} placeholder="Brief description of your business..."
                    rows={3} className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none resize-none" style={inputStyle} />
                </div>
              </div>
            )}

            {/* Step 2: Contact Details */}
            {regStep === 2 && (
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-white mb-4" style={{ fontFamily: 'DM Sans, sans-serif' }}>Contact Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={labelStyle}>First Name <span style={{ color: '#EF4444' }}>*</span></label>
                    <input type="text" value={regForm?.contact_first_name} onChange={e => setRegForm(f => ({ ...f, contact_first_name: e?.target?.value }))} placeholder="First name"
                      className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={labelStyle}>Last Name <span style={{ color: '#EF4444' }}>*</span></label>
                    <input type="text" value={regForm?.contact_last_name} onChange={e => setRegForm(f => ({ ...f, contact_last_name: e?.target?.value }))} placeholder="Last name"
                      className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={labelStyle}>Business Email <span style={{ color: '#EF4444' }}>*</span></label>
                  <input type="email" value={regForm?.contact_email} onChange={e => setRegForm(f => ({ ...f, contact_email: e?.target?.value }))} placeholder="you@company.com"
                    className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={labelStyle}>Phone Number</label>
                  <input type="tel" value={regForm?.phone} onChange={e => setRegForm(f => ({ ...f, phone: e?.target?.value }))} placeholder="+1 555 000 0000"
                    className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={labelStyle}>Country</label>
                    <select value={regForm?.country} onChange={e => setRegForm(f => ({ ...f, country: e?.target?.value }))}
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none" style={{ ...inputStyle, color: regForm?.country ? '#E2E8F0' : '#64748B' }}>
                      <option value="" style={{ background: '#0D1B2A' }}>Select country</option>
                      {COUNTRIES?.map(c => <option key={c} value={c} style={{ background: '#0D1B2A' }}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={labelStyle}>City</label>
                    <input type="text" value={regForm?.city} onChange={e => setRegForm(f => ({ ...f, city: e?.target?.value }))} placeholder="Your city"
                      className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Partnership Details */}
            {regStep === 3 && (
              <div className="space-y-5">
                <h3 className="text-base font-semibold text-white mb-4" style={{ fontFamily: 'DM Sans, sans-serif' }}>Partnership Details</h3>
                <div>
                  <label className="block text-xs font-medium mb-3" style={labelStyle}>Preferred Partner Tier</label>
                  <div className="space-y-2">
                    {TIERS?.map(tier => (
                      <button key={tier?.id} type="button" onClick={() => setRegForm(f => ({ ...f, preferred_tier: tier?.id }))}
                        className="w-full p-3.5 rounded-xl text-left transition-all relative"
                        style={{
                          background: regForm?.preferred_tier === tier?.id ? `${tier?.color}12` : 'rgba(0,0,0,0.2)',
                          border: regForm?.preferred_tier === tier?.id ? `1px solid ${tier?.color}55` : '1px solid rgba(255,255,255,0.06)',
                        }}>
                        {tier?.popular && (
                          <span className="absolute top-2 right-3 text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,229,255,0.15)', color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>Popular</span>
                        )}
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm font-bold" style={{ color: tier?.color, fontFamily: 'DM Sans, sans-serif' }}>{tier?.name}</span>
                          <span className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{tier?.price}</span>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                          {tier?.features?.map(f => (
                            <span key={f} className="text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>• {f}</span>
                          ))}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={labelStyle}>How did you hear about us? <span style={{ color: '#EF4444' }}>*</span></label>
                    <select value={regForm?.referral_source} onChange={e => setRegForm(f => ({ ...f, referral_source: e?.target?.value }))}
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none" style={{ ...inputStyle, color: regForm?.referral_source ? '#E2E8F0' : '#64748B' }}>
                      <option value="" style={{ background: '#0D1B2A' }}>Select source</option>
                      {REFERRAL_SOURCES?.map(s => <option key={s} value={s} style={{ background: '#0D1B2A' }}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={labelStyle}>Expected monthly scans <span style={{ color: '#EF4444' }}>*</span></label>
                    <select value={regForm?.expected_scans} onChange={e => setRegForm(f => ({ ...f, expected_scans: e?.target?.value }))}
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none" style={{ ...inputStyle, color: regForm?.expected_scans ? '#E2E8F0' : '#64748B' }}>
                      <option value="" style={{ background: '#0D1B2A' }}>Select range</option>
                      {EXPECTED_SCANS?.map(s => <option key={s} value={s} style={{ background: '#0D1B2A' }}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <div
                      onClick={() => setRegForm(f => ({ ...f, agreed: !f?.agreed }))}
                      className="mt-0.5 w-5 h-5 rounded flex-shrink-0 flex items-center justify-center transition-all cursor-pointer"
                      style={{
                        background: regForm?.agreed ? 'linear-gradient(135deg, #00E5FF, #7B2FBE)' : 'rgba(0,0,0,0.3)',
                        border: regForm?.agreed ? '1px solid #00E5FF' : '1px solid rgba(0,229,255,0.2)',
                      }}
                    >
                      {regForm?.agreed && <CheckCircle size={12} className="text-white" />}
                    </div>
                    <span className="text-xs leading-relaxed" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>
                      I agree to the <span style={{ color: '#00E5FF' }}>FinVisAR Partner Terms & Conditions</span> and confirm that all information provided is accurate.
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-3 mt-6">
              {regStep > 1 && (
                <button type="button" onClick={() => { setRegStep(s => s - 1); setRegError(''); }}
                  className="flex-1 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                  style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>
                  <ArrowLeft size={15} /> Back
                </button>
              )}
              {regStep < 3 ? (
                <button type="button" onClick={handleNextStep}
                  className="flex-1 py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2"
                  style={{ background: 'linear-gradient(135deg, #00E5FF, #7B2FBE)', fontFamily: 'DM Sans, sans-serif' }}>
                  Continue <ArrowRight size={15} />
                </button>
              ) : (
                <button type="button" onClick={handleSubmitApplication} disabled={loading}
                  className="flex-1 py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2"
                  style={{ background: loading ? 'rgba(0,229,255,0.3)' : 'linear-gradient(135deg, #00E5FF, #7B2FBE)', fontFamily: 'DM Sans, sans-serif', opacity: loading ? 0.7 : 1 }}>
                  {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : null}
                  {loading ? 'Submitting...' : 'Submit Application'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── SUCCESS SCREEN ── */}
        {authMode === 'success' && submittedApp && (
          <div className="rounded-2xl p-8 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(16,185,129,0.3)', backdropFilter: 'blur(20px)' }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: 'rgba(16,185,129,0.15)', border: '2px solid rgba(16,185,129,0.4)' }}>
              <CheckCircle size={32} style={{ color: '#10B981' }} />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>Application Submitted! 🎉</h2>
            <p className="text-sm mb-5" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{submittedApp?.company_name}</p>
            <div className="rounded-xl p-4 mb-5 text-left" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.12)' }}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Reference Number</span>
                <span className="text-sm font-bold" style={{ color: '#00E5FF', fontFamily: 'DM Sans, sans-serif', fontFamily: 'monospace' }}>#{submittedApp?.id?.substring(0, 8)?.toUpperCase()}</span>
              </div>
              <p className="text-xs leading-relaxed" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>
                Our team will review your application within <strong style={{ color: '#E2E8F0' }}>2 business days</strong>. You'll receive an email at{' '}
                <strong style={{ color: '#00E5FF' }}>{submittedApp?.contact_email}</strong> with next steps.
              </p>
            </div>
            <button onClick={() => switchAuthMode('login')}
              className="w-full py-3 rounded-xl text-sm font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, #00E5FF, #7B2FBE)', fontFamily: 'DM Sans, sans-serif' }}>
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
